/**
 * Auto-Fill Service
 *
 * Pattern matching engine for automatically populating PCF attributes
 * based on Revit family names. Analyzes family names to extract:
 * - OBJECT-TYPE (component category)
 * - SKEY (symbol key with angle/size extraction)
 * - END-TYPE (connection method)
 */

import {
  AUTO_FILL_PATTERNS,
  END_TYPE_PATTERNS,
  ANGLE_REGEX,
  COMMON_ANGLES,
  getMatchQualityMultiplier,
  type AutoFillPattern,
  type EndTypePattern,
} from '../../config/autoFillPatterns';
import {
  normalizeObjectTypeKey,
  resolvePreferredEtalonSkey,
  resolveEtalonEndTypes,
} from '../../config/pcfEtalonCatalog';
import {
  getCompatibleObjectTypeValuesForCategory,
  resolvePcfObjectTypeCategoryKind,
} from '../../config/objectTypeCategoryCompatibility';
import { normalizeAutoFillFamilyName } from '../quickFill/familyNameMatching';
import { matchLearnedAutoFillPattern } from '../quickFill/learnedAutoFillLibrary';
import { useLearnedAutoFillLibraryStore } from '../../stores/useLearnedAutoFillLibraryStore';

/**
 * Result of auto-fill analysis for a single family
 */
export interface AutoFillResult {
  /** PCF OBJECT-TYPE value */
  objectType?: string;
  /** Symbol Key */
  skey?: string;
  /** End connection type for port 1 */
  end1?: string;
  /** End connection type for port 2 */
  end2?: string;
  /** End connection type for port 3 (for tees, etc.) */
  end3?: string;
  /** End connection type for port 4 (for crosses) */
  end4?: string;
  /** Confidence score (0-100) */
  confidence: number;
  /** Pattern ID that matched */
  matchedPatternId?: string;
  /** Raw keywords that matched */
  matchedKeywords: string[];
  /** Extracted angle value, if any */
  extractedAngle?: number;
}

export interface AutoFillFromFamilyNameOptions {
  includeLearned?: boolean;
}

/**
 * Batch result for multiple families
 */
export interface BatchAutoFillResult {
  /** Total families processed */
  total: number;
  /** Families with matches found */
  matched: number;
  /** Families with high confidence (>70%) */
  highConfidence: number;
  /** Individual results mapped by family name */
  results: Map<string, AutoFillResult>;
}

export type QuickFillAutoFillMode = 'overwrite' | 'fillEmpty';

const normalizeFamilyName = normalizeAutoFillFamilyName;

/**
 * Extracts angle value from a family name
 * Returns the first angle found, or undefined if none
 */
function extractAngle(familyName: string): number | undefined {
  // Reset regex state
  ANGLE_REGEX.lastIndex = 0;

  const matches: number[] = [];
  let match;

  while ((match = ANGLE_REGEX.exec(familyName)) !== null) {
    const angle = parseFloat(match[1]);
    // Only consider valid pipe angles
    if (angle > 0 && angle <= 180 && isAngleCandidateContext(familyName, match)) {
      matches.push(angle);
    }
  }

  if (matches.length === 0) return undefined;

  // Prefer common angles
  for (const angle of matches) {
    if (COMMON_ANGLES.includes(angle)) {
      return angle;
    }
  }

  // Return first found angle
  return matches[0];
}

const ANGLE_PREFIX_SKIP_TOKENS = new Set([
  'class',
  'cl',
  'sch',
  'schedule',
  'fig',
  'figure',
  'length',
  'len',
  'thk',
  'thickness',
  'type',
  'style',
  'size',
  'dn',
  'nb',
  'nps',
  'v',
]);

const ANGLE_SUFFIX_SKIP_TOKENS = new Set([
  'in',
  'inch',
  'inches',
  'mm',
  'dn',
  'nb',
  'nps',
  'lb',
  'lbs',
  'pc',
  'pcs',
]);

function getNormalizedTokens(value: string): string[] {
  return normalizeFamilyName(value).split(/\s+/).filter(Boolean);
}

function isAngleCandidateContext(familyName: string, match: RegExpExecArray): boolean {
  const matchedText = match[0];
  const start = match.index;
  const end = start + matchedText.length;
  const beforeChar = start > 0 ? familyName[start - 1] : '';
  const afterChar = end < familyName.length ? familyName[end] : '';
  const beforeText = familyName.slice(Math.max(0, start - 40), start);
  const afterText = familyName.slice(end, Math.min(familyName.length, end + 40));
  const hasAngleUnit = Boolean(match[2]) || /^\s*[- ]?\s*(?:°|deg|degree|grad|градус)\b/i.test(afterText);

  if (/[._]/.test(beforeChar) || /[._]/.test(afterChar)) {
    return false;
  }

  if (beforeChar === '-' && /\d/.test(familyName[start - 2] ?? '')) {
    return false;
  }

  if (afterChar === '-' && /\d/.test(familyName[end + 1] ?? '')) {
    return false;
  }

  if (!hasAngleUnit && /[A-Za-z]/.test(beforeChar)) {
    return false;
  }

  const prefixTokens = getNormalizedTokens(beforeText).slice(-2);
  if (prefixTokens.some((token) => ANGLE_PREFIX_SKIP_TOKENS.has(token))) {
    return false;
  }

  if (!hasAngleUnit) {
    const suffixToken = getNormalizedTokens(afterText)[0];
    if (suffixToken && ANGLE_SUFFIX_SKIP_TOKENS.has(suffixToken)) {
      return false;
    }
  }

  return true;
}

function shouldResolveAnglePlaceholder(skey: string): boolean {
  return /^(EL|EB|ER|EU|BE|MI|BU|PB|BT|TY)\?\?$/i.test(skey);
}

/**
 * Resolves SKEY placeholder with extracted angle
 * EL?? + 90 = EL90
 */
function resolveSkeyWithAngle(skey: string, angle?: number): string {
  if (!angle || !skey.includes('??') || !shouldResolveAnglePlaceholder(skey)) {
    return skey;
  }

  // Format angle (remove decimal if whole number)
  const angleStr = angle % 1 === 0 ? angle.toString() : angle.toFixed(1);

  return skey.replace('??', angleStr);
}

function normalizeAutoFillEndType(
  endType: string | undefined,
  objectType?: string,
  skey?: string
): string | undefined {
  if (!endType) {
    return undefined;
  }

  if (endType !== 'UNDEFINED_ET') {
    return endType;
  }

  const normalizedObjectType = normalizeObjectTypeKey(objectType);
  const normalizedSkey = (skey ?? '').trim().toUpperCase();
  const preserveUppercaseUndefinedObjectTypes = new Set(['END-CONNECTION-EQUIPMENT', 'REFERENCE-POINT']);
  const preserveUppercaseUndefinedSupportSkeys = new Set(['SUPP', 'O1HG', 'ANCH', 'GUID', 'SPRG', 'HANG', 'SKID', 'PSIG', 'DUCK']);
  if (preserveUppercaseUndefinedObjectTypes.has(normalizedObjectType)) {
    return 'UNDEFINED_ET';
  }

  if (
    normalizedObjectType === 'SUPPORT' &&
    preserveUppercaseUndefinedSupportSkeys.has(normalizedSkey)
  ) {
    return 'UNDEFINED_ET';
  }

  return 'Undefined_ET';
}

function shouldRequireTokenMatch(keyword: string): boolean {
  return /^[a-z0-9]+$/i.test(keyword);
}

interface PreparedAutoFillKeyword {
  original: string;
  normalized: string;
  tokens: string[];
  requiresTokenMatch: boolean;
}

const prepareAutoFillKeywords = (keywords: string[]): PreparedAutoFillKeyword[] =>
  keywords.map((original) => {
    const normalized = normalizeFamilyName(original);
    return {
      original,
      normalized,
      tokens: normalized.split(/\s+/).filter(Boolean),
      requiresTokenMatch: shouldRequireTokenMatch(normalized),
    };
  });

// The pattern libraries are static. Normalizing every keyword for every row is
// disproportionately expensive on the large Quick Fill grid, so prepare them
// once while retaining the exact same matching rules at call time.
const preparedAutoFillPatternKeywords = new Map<AutoFillPattern, PreparedAutoFillKeyword[]>(
  AUTO_FILL_PATTERNS.map((pattern) => [pattern, prepareAutoFillKeywords(pattern.keywords)])
);
const preparedEndTypePatternKeywords = new Map<EndTypePattern, PreparedAutoFillKeyword[]>(
  END_TYPE_PATTERNS.map((pattern) => [pattern, prepareAutoFillKeywords(pattern.keywords)])
);

function matchesPreparedKeyword(
  keyword: PreparedAutoFillKeyword,
  normalizedName: string,
  tokens: string[]
): boolean {
  if (keyword.requiresTokenMatch) {
    return tokens.includes(keyword.normalized);
  }

  if (normalizedName.includes(keyword.normalized)) {
    return true;
  }

  return keyword.tokens.length > 1 && keyword.tokens.every((token) => tokens.includes(token));
}

/**
 * Calculates match score for a pattern against a family name
 */
function calculatePatternScore(
  pattern: AutoFillPattern,
  normalizedName: string,
  originalName: string,
  tokens: string[]
): { score: number; matchedKeywords: string[] } {
  let bestScore = 0;
  const matchedKeywords: string[] = [];
  const preparedKeywords = preparedAutoFillPatternKeywords.get(pattern) ??
    prepareAutoFillKeywords(pattern.keywords);

  for (const keyword of preparedKeywords) {
    // Check for match
    if (matchesPreparedKeyword(keyword, normalizedName, tokens)) {
      // Check match quality
      const isExactMatch = normalizedName === keyword.normalized ||
        tokens.includes(keyword.normalized) ||
        (keyword.tokens.length > 1 && keyword.tokens.every((token) => tokens.includes(token)));

      const qualityMultiplier = getMatchQualityMultiplier(
        keyword.normalized,
        originalName,
        isExactMatch
      );

      // Longer keywords are more specific, score higher
      const lengthBonus = Math.min(keyword.original.length / 10, 1);

      const score = pattern.priority * qualityMultiplier * (1 + lengthBonus);

      if (score > bestScore) {
        bestScore = score;
      }

      matchedKeywords.push(keyword.original);
    }
  }

  return { score: bestScore, matchedKeywords };
}

/**
 * Detects end type from family name
 */
function detectEndType(normalizedName: string, tokens: string[]): string | undefined {
  let bestMatch: { endType: string; score: number } | undefined;

  for (const pattern of END_TYPE_PATTERNS) {
    const preparedKeywords = preparedEndTypePatternKeywords.get(pattern) ??
      prepareAutoFillKeywords(pattern.keywords);
    for (const keyword of preparedKeywords) {

      if (matchesPreparedKeyword(keyword, normalizedName, tokens)) {
        // Word boundary match scores higher
        const wordBoundaryRegex = new RegExp(`\\b${keyword.normalized}\\b`, 'i');
        const isWordMatch = wordBoundaryRegex.test(normalizedName);

        const score = pattern.priority * (isWordMatch ? 1.5 : 1);

        if (!bestMatch || score > bestMatch.score) {
          bestMatch = { endType: pattern.endType, score };
        }
      }
    }
  }

  return bestMatch?.endType;
}

/**
 * Counts the number of connection ports based on object type
 */
function getPortCount(objectType: string): number {
  const normalizedObjectType = normalizeObjectTypeKey(objectType);

  switch (normalizedObjectType) {
    case 'PIPE':
      return 2;
    case 'ELBOW':
    case 'BEND':
    case 'REDUCER-CONCENTRIC':
    case 'REDUCER-ECCENTRIC':
    case 'COUPLING':
    case 'UNION':
    case 'FLANGE':
    case 'VALVE':
    case 'GASKET':
    case 'EXPANSION-JOINT':
    case 'SPECTACLE-BLIND':
      return 2;
    case 'TEE':
      return 3;
    case 'CROSS':
      return 4;
    case 'CAP':
    case 'PLUG':
    case 'FLANGE-BLIND':
      return 1;
    case 'SUPPORT':
    case 'INSTRUMENT':
    case 'FILTER':
    case 'ORIFICE':
    default:
      return 2;
  }
}

function splitFamilyAndTypeName(fullName: string): { familyPart: string; typePart: string } {
  const separatorIndex = fullName.indexOf(':');
  if (separatorIndex < 0) {
    return { familyPart: fullName, typePart: '' };
  }

  return {
    familyPart: fullName.slice(0, separatorIndex),
    typePart: fullName.slice(separatorIndex + 1),
  };
}

function hasNormalizedToken(normalizedValue: string, token: string): boolean {
  return normalizedValue.split(/\s+/).filter(Boolean).includes(token);
}

function hasAnyNormalizedToken(normalizedValue: string, tokens: string[]): boolean {
  return tokens.some((token) => hasNormalizedToken(normalizedValue, token));
}

function includesAnyNormalizedPhrase(normalizedValue: string, phrases: string[]): boolean {
  return phrases.some((phrase) => normalizedValue.includes(phrase));
}

function buildCombinedFamilyTypeText(normalizedFamilyPart: string, normalizedTypePart: string): string {
  return `${normalizedFamilyPart} ${normalizedTypePart}`.trim();
}

function includesExplicitComponentMarker(normalizedFamilyPart: string, normalizedTypePart: string): boolean {
  const normalizedCombined = buildCombinedFamilyTypeText(normalizedFamilyPart, normalizedTypePart);
  const tokenMarkers = [
    'valve',
    'trap',
    'ferrule',
    'coupling',
    'cplg',
    'olet',
    'anvilet',
    'support',
    'hanger',
    'rod',
    'saddle',
    'channel',
    'gasket',
    'weld',
  ];
  const phraseMarkers = [
    'bolt set',
    'weld gap',
    'pipe roll',
    'u bolt',
    'threaded rod',
  ];

  return (
    hasAnyNormalizedToken(normalizedCombined, tokenMarkers) ||
    includesAnyNormalizedPhrase(normalizedCombined, phraseMarkers)
  );
}

function includesEquipmentFamilyMarker(normalizedFamilyPart: string, normalizedTypePart: string): boolean {
  const normalizedCombined = buildCombinedFamilyTypeText(normalizedFamilyPart, normalizedTypePart);
  if (!normalizedCombined) {
    return false;
  }

  const tokenMarkers = [
    'ahu',
    'idf',
    'mdf',
    'pump',
    'vessel',
    'decanter',
    'compressor',
    'blower',
    'reactor',
    'column',
    'separator',
    'scrubber',
    'agitator',
    'mixer',
    'tank',
    'drum',
    'heater',
    'cooler',
  ];
  const phraseMarkers = [
    'air handling unit',
    'heat exchanger',
  ];

  return (
    hasAnyNormalizedToken(normalizedCombined, tokenMarkers) ||
    includesAnyNormalizedPhrase(normalizedCombined, phraseMarkers)
  );
}

function buildStructuredAutoFillResult(args: {
  objectType: string;
  skey?: string;
  matchedPatternId: string;
  matchedKeywords: string[];
  confidence?: number;
  end1?: string;
  end2?: string;
  end3?: string;
  end4?: string;
  preserveSkey?: boolean;
}): AutoFillResult {
  const objectType = normalizeObjectTypeKey(args.objectType);
  const skey = args.skey
    ? (args.preserveSkey ? args.skey : (resolvePreferredEtalonSkey(objectType, args.skey) ?? args.skey))
    : undefined;
  const etalonEndTypes = resolveEtalonEndTypes(objectType, skey ?? '') ?? undefined;

  return {
    objectType,
    skey,
    confidence: args.confidence ?? 100,
    matchedPatternId: args.matchedPatternId,
    matchedKeywords: args.matchedKeywords,
    end1: normalizeAutoFillEndType(args.end1 ?? etalonEndTypes?.end1, objectType, skey),
    end2: normalizeAutoFillEndType(args.end2 ?? etalonEndTypes?.end2, objectType, skey),
    end3: normalizeAutoFillEndType(args.end3 ?? etalonEndTypes?.end3, objectType, skey),
    end4: normalizeAutoFillEndType(args.end4 ?? etalonEndTypes?.end4, objectType, skey),
  };
}

function buildCatalogSpecificAutoFillResult(args: {
  objectType: string;
  skey?: string;
  matchedKeywords: string[];
  end1?: string;
  end2?: string;
}): AutoFillResult {
  return buildStructuredAutoFillResult({
    ...args,
    matchedPatternId: 'structured-catalog-specific-family',
    confidence: 100,
    preserveSkey: true,
  });
}

function buildHardIgnoreAutoFillResult(matchedPatternId: string, matchedKeywords: string[]): AutoFillResult {
  return {
    objectType: 'IGNORE',
    skey: '',
    end1: 'Undefined_ET',
    end2: 'Undefined_ET',
    confidence: 100,
    matchedPatternId,
    matchedKeywords,
  };
}

function isIgnoreEmptySkeyAutoFillValue(
  result: AutoFillResult,
  label: string,
  nextValue: unknown,
  mode: QuickFillAutoFillMode
): boolean {
  return mode === 'overwrite' &&
    normalizeObjectTypeKey(result.objectType) === 'IGNORE' &&
    isSkeyAutoFillColumn(label) &&
    nextValue === '';
}

function resolveWeldGapKeyword(normalizedFamilyPart: string): string | null {
  if (normalizedFamilyPart.includes('field weld gap')) {
    return 'field weld gap';
  }

  if (normalizedFamilyPart.includes('orbital weld gap')) {
    return 'orbital weld gap';
  }

  if (normalizedFamilyPart.includes('shop weld gap')) {
    return 'shop weld gap';
  }

  if (normalizedFamilyPart.includes('weld gap')) {
    return 'weld gap';
  }

  return null;
}

function resolveCatalogSpecificFamilyResult(
  normalizedFamilyPart: string,
  normalizedTypePart: string
): AutoFillResult | null {
  if (normalizedFamilyPart === 'i elbow generic v2024 1 102') {
    return buildCatalogSpecificAutoFillResult({
      objectType: 'ELBOW',
      skey: 'EL??',
      matchedKeywords: ['i elbow generic v2024 1 102'],
    });
  }

  if (normalizedFamilyPart === 'i coupling generic centerbackmoved v2024 1 102') {
    return buildCatalogSpecificAutoFillResult({
      objectType: 'COUPLING',
      skey: 'CO??',
      matchedKeywords: ['i coupling generic centerbackmoved v2024 1 102'],
    });
  }

  if (
    normalizedFamilyPart.startsWith('penn machine cs sch80 nipple') ||
    normalizedFamilyPart.startsWith('legend galv cs t 575 dielectric nipple')
  ) {
    return buildCatalogSpecificAutoFillResult({
      objectType: 'COUPLING',
      skey: 'NR??',
      end1: 'PL',
      end2: 'PL',
      matchedKeywords: ['sample nipple coupling'],
    });
  }

  if (
    (normalizedFamilyPart === 'gtp' && normalizedTypePart === 'pipe pt') ||
    normalizedFamilyPart === 'fab pro spool id' ||
    normalizedFamilyPart.startsWith('plate washer anvil') ||
    normalizedFamilyPart.startsWith('machine threaded rod anvil')
  ) {
    return buildHardIgnoreAutoFillResult('structured-non-pcf-helper', [normalizedFamilyPart]);
  }

  if (
    normalizedFamilyPart.startsWith('asc cs 320 blk smls nipple') ||
    normalizedFamilyPart.startsWith('beck cs sch std nipple smls') ||
    normalizedFamilyPart.startsWith('anvil cs sch 80 338 welded pipe nipple')
  ) {
    return buildCatalogSpecificAutoFillResult({
      objectType: 'COUPLING',
      skey: 'CO??',
      end1: 'BV',
      end2: 'BV',
      matchedKeywords: ['threaded pipe nipple coupling'],
    });
  }

  if (/^(?:hammond )?ir(?:1140|116) hi \d{3}$/.test(normalizedFamilyPart)) {
    return buildCatalogSpecificAutoFillResult({
      objectType: 'VALVE',
      skey: 'VT??',
      matchedKeywords: ['hammond ir hi valve'],
    });
  }

  const keckleyStrainerSkeys: Array<[string, string]> = [
    ['keckley 33 rfy ci a7 strainer', '11SP'],
    ['keckley 43 rfy ci a7 strainer', '12SP'],
    ['keckley brz f 150 y strainer', '14SP'],
    ['keckley 63 rfy ci a7 strainer', '15SP'],
  ];
  for (const [familyMarker, skey] of keckleyStrainerSkeys) {
    if (normalizedFamilyPart.includes(familyMarker)) {
      return buildCatalogSpecificAutoFillResult({
        objectType: 'VALVE',
        skey,
        end1: 'FL',
        end2: 'FL',
        matchedKeywords: [familyMarker],
      });
    }
  }

  if (hasNormalizedToken(normalizedFamilyPart, 'gasket')) {
    return buildCatalogSpecificAutoFillResult({
      objectType: 'GASKET',
      skey: 'GASK',
      matchedKeywords: ['gasket'],
    });
  }

  if (
    normalizedFamilyPart === 'pipe roll adjustable steel yoke anvil 181' &&
    normalizedTypePart.includes('fig 181 cast iron plain')
  ) {
    return buildHardIgnoreAutoFillResult('structured-non-pcf-helper', [normalizedFamilyPart, normalizedTypePart]);
  }

  if (
    normalizedFamilyPart === 'pipe roll adjustable steel yoke anvil 181' &&
    normalizedTypePart.includes('fig 181 cast iron zinc plated')
  ) {
    return buildCatalogSpecificAutoFillResult({
      objectType: 'SUPPORT',
      skey: 'PSIG',
      matchedKeywords: ['pipe roll adjustable steel yoke anvil 181'],
    });
  }

  if (normalizedFamilyPart === 'asc ci 171 ztc pipe roll') {
    return buildCatalogSpecificAutoFillResult({
      objectType: 'SUPPORT',
      skey: 'PSIG',
      matchedKeywords: ['asc ci 171 ztc pipe roll'],
    });
  }

  if (normalizedFamilyPart.startsWith('anvil cs fig 181 adj pipe roll')) {
    return buildCatalogSpecificAutoFillResult({
      objectType: 'SUPPORT',
      skey: 'GUID',
      matchedKeywords: ['anvil cs fig 181 adj pipe roll'],
    });
  }

  if (normalizedFamilyPart === 'hfi pipe saddle') {
    return buildCatalogSpecificAutoFillResult({
      objectType: 'SUPPORT',
      skey: 'SUPP',
      matchedKeywords: ['hfi pipe saddle'],
    });
  }

  if (
    normalizedFamilyPart.startsWith('pipe covering protection saddle anvil') &&
    normalizedTypePart.includes('fig 164 carbon steel')
  ) {
    return buildHardIgnoreAutoFillResult('structured-non-pcf-helper', [normalizedFamilyPart, normalizedTypePart]);
  }

  if (normalizedFamilyPart.startsWith('pipe covering protection saddle anvil')) {
    return buildCatalogSpecificAutoFillResult({
      objectType: 'SUPPORT',
      skey: 'SUPP',
      matchedKeywords: ['pipe covering protection saddle'],
    });
  }

  if (
    normalizedFamilyPart.includes('u bolt') ||
    normalizedFamilyPart.includes('u-bolt') ||
    normalizedFamilyPart.includes('fig 137ss u bolt')
  ) {
    return buildCatalogSpecificAutoFillResult({
      objectType: 'SUPPORT',
      skey: 'HANG',
      matchedKeywords: ['u bolt'],
    });
  }

  if (
    (normalizedFamilyPart.includes('strongback') || normalizedFamilyPart.includes('strong back')) &&
    normalizedFamilyPart.includes('channel')
  ) {
    return buildCatalogSpecificAutoFillResult({
      objectType: 'SUPPORT',
      skey: 'SUPP',
      matchedKeywords: ['strongback channel'],
    });
  }

  if (hasNormalizedToken(normalizedFamilyPart, 'anvilet')) {
    return buildCatalogSpecificAutoFillResult({
      objectType: 'OLET',
      skey: 'WTBW',
      matchedKeywords: ['anvilet'],
    });
  }

  if (hasNormalizedToken(normalizedFamilyPart, 'cplg')) {
    return buildCatalogSpecificAutoFillResult({
      objectType: 'COUPLING',
      skey: 'CO??',
      matchedKeywords: ['cplg'],
    });
  }

  if (
    hasNormalizedToken(normalizedFamilyPart, 'ferrule') &&
    hasNormalizedToken(normalizedFamilyPart, 'clamp')
  ) {
    return buildCatalogSpecificAutoFillResult({
      objectType: 'COUPLING',
      skey: 'COSC',
      end1: 'SW',
      end2: 'SW',
      matchedKeywords: ['clamp ferrule'],
    });
  }

  if (normalizedFamilyPart === 'mtflx cs pgpre 3 inch insulated spider guide') {
    return buildCatalogSpecificAutoFillResult({
      objectType: 'SUPPORT',
      skey: 'GUID',
      end1: 'UNDEFINED_ET',
      end2: 'UNDEFINED_ET',
      matchedKeywords: ['insulated spider guide'],
    });
  }

  if (/^viega brz 291[12] zl adapter p rx (?:mpt|fpt)$/.test(normalizedFamilyPart)) {
    return buildHardIgnoreAutoFillResult('structured-non-pcf-helper', ['viega brz adapter prx']);
  }

  if (normalizedFamilyPart === 'viega cu 2915 stop coupling p rx pr') {
    return buildCatalogSpecificAutoFillResult({
      objectType: 'COUPLING',
      skey: 'CO??',
      matchedKeywords: ['viega cu 2915 stop coupling'],
    });
  }

  if (normalizedFamilyPart.includes('viega cu 0959 5 xl adapter flange')) {
    return buildCatalogSpecificAutoFillResult({
      objectType: 'FLANGE',
      skey: 'FLSO',
      end1: 'BV',
      end2: 'FL',
      matchedKeywords: ['viega cu 0959 5xl adapter flange'],
    });
  }

  if (normalizedFamilyPart.includes('anvil mi sch xh 1167 reducer blk aar')) {
    return buildCatalogSpecificAutoFillResult({
      objectType: 'REDUCER-CONCENTRIC',
      skey: 'RB??',
      end1: 'BV',
      end2: 'BV',
      matchedKeywords: ['anvil mi sch xh 1167 reducer blk aar'],
    });
  }

  return null;
}

function resolveStructuredFamilyTypeResult(fullName: string): AutoFillResult | null {
  const { familyPart, typePart } = splitFamilyAndTypeName(fullName);
  const normalizedFamilyPart = normalizeFamilyName(familyPart);
  const normalizedTypePart = normalizeFamilyName(typePart);
  const normalizedTypeKey = normalizeObjectTypeKey(typePart);
  const catalogSpecificResult = resolveCatalogSpecificFamilyResult(normalizedFamilyPart, normalizedTypePart);
  if (catalogSpecificResult) {
    return catalogSpecificResult;
  }

  if (normalizedFamilyPart.includes('iso note')) {
    const isoNoteVariants: Record<string, Omit<AutoFillResult, 'confidence' | 'matchedKeywords' | 'matchedPatternId'>> = {
      'MESSAGE-ROUND': { objectType: 'MESSAGE-ROUND' },
      'BIP-IDENTIFIER': { objectType: 'BIP-IDENTIFIER' },
      'MESSAGE-ELLIPSE': { objectType: 'MESSAGE-ELLIPSE', skey: 'EL??', end1: 'BV', end2: 'BV' },
      'MESSAGE-DOUBLE-CIRCLE': { objectType: 'MESSAGE-DOUBLE-CIRCLE' },
      'MESSAGE-TRIANGLE': { objectType: 'MESSAGE-TRIANGLE' },
      'MESSAGE-CIRCLE': { objectType: 'MESSAGE-CIRCLE' },
      'MESSAGE-SQUARE': { objectType: 'MESSAGE-SQUARE' },
      'MESSAGE': { objectType: 'MESSAGE' },
      'START-CO-ORDS': { objectType: 'START-CO-ORDS' },
      'LOCATION-POINT': { objectType: 'LOCATION-POINT' },
      'MESSAGE-DIAMOND': { objectType: 'MESSAGE-DIAMOND' },
    };

    const variant = isoNoteVariants[normalizedTypeKey];
    if (variant?.objectType) {
      return buildStructuredAutoFillResult({
        objectType: variant.objectType,
        skey: variant.skey,
        end1: variant.end1,
        end2: variant.end2,
        matchedPatternId: 'structured-iso-note',
        matchedKeywords: [normalizedTypeKey],
      });
    }
  }

  if (normalizedFamilyPart.includes('reference point')) {
    const referencePointVariants: Record<string, { skey: string; end1?: string; end2?: string }> = {
      WALL: { skey: 'WAL5' },
      PIPE: { skey: 'PIP4', end1: 'PL', end2: 'PL' },
      BUILDING: { skey: 'BLD2' },
      EQUIPMENT: { skey: 'EQU2' },
      FLOOR: { skey: 'FLR5' },
      MISC: { skey: 'XXX4' },
      BEAM: { skey: 'HST4' },
      COLUMN: { skey: 'VST4' },
    };

    const variant = referencePointVariants[normalizedTypeKey];
    if (variant) {
      return buildStructuredAutoFillResult({
        objectType: 'REFERENCE-POINT',
        skey: variant.skey,
        end1: variant.end1,
        end2: variant.end2,
        matchedPatternId: 'structured-reference-point',
        matchedKeywords: [normalizedTypeKey],
      });
    }

    return buildStructuredAutoFillResult({
      objectType: 'REFERENCE-POINT',
      skey: 'EQU2',
      end1: 'UNDEFINED_ET',
      end2: 'UNDEFINED_ET',
      matchedPatternId: 'structured-reference-point-default',
      matchedKeywords: ['reference point'],
    });
  }

  if (normalizedFamilyPart === 'flange') {
    return buildStructuredAutoFillResult({
      objectType: 'FLANGE',
      skey: 'FLSO',
      matchedPatternId: 'structured-flange-family',
      matchedKeywords: ['flange'],
    });
  }

  if (normalizedFamilyPart === 'stub in') {
    return buildStructuredAutoFillResult({
      objectType: 'LAPJOINT-STUBEND',
      skey: 'FLSE',
      matchedPatternId: 'structured-stub-in',
      matchedKeywords: ['stub in'],
    });
  }

  if (normalizedFamilyPart.includes('tap connection') || normalizedFamilyPart.includes('piquage')) {
    const welded = normalizedFamilyPart.includes('soude') || normalizedFamilyPart.includes('weld');
    const pipeLike = normalizedFamilyPart.includes('pipe');
    return buildStructuredAutoFillResult({
      objectType: 'TAP-CONNECTION',
      matchedPatternId: 'structured-tap-connection',
      matchedKeywords: pipeLike ? ['tap connection', 'pipe'] : ['tap connection'],
      end1: welded ? 'BV' : (pipeLike ? 'PL' : undefined),
      end2: welded ? 'BV' : (pipeLike ? 'PL' : undefined),
    });
  }

  if (normalizedTypePart.includes('tap connection')) {
    return buildStructuredAutoFillResult({
      objectType: 'TAP-CONNECTION',
      matchedPatternId: 'structured-tap-connection-type',
      matchedKeywords: ['tap connection'],
      end1: normalizedFamilyPart.includes('pipe') ? 'PL' : undefined,
      end2: normalizedFamilyPart.includes('pipe') ? 'PL' : undefined,
    });
  }

  if (normalizedTypePart.includes('auto weld')) {
    return buildStructuredAutoFillResult({
      objectType: 'WELD',
      skey: 'BV',
      matchedPatternId: 'structured-auto-weld',
      matchedKeywords: ['auto weld'],
    });
  }

  const weldGapKeyword = resolveWeldGapKeyword(normalizedFamilyPart);
  if (weldGapKeyword) {
    return buildHardIgnoreAutoFillResult('structured-weld-gap-helper', [weldGapKeyword]);
  }

  if (normalizedFamilyPart === 'pipes') {
    return buildStructuredAutoFillResult({
      objectType: 'PIPE',
      matchedPatternId: 'structured-pipes-family',
      matchedKeywords: ['pipes'],
      end1: 'PL',
      end2: 'PL',
    });
  }

  if (
    normalizedFamilyPart.includes('e m point') ||
    normalizedFamilyPart.includes('em point') ||
    normalizedFamilyPart.includes('vic point')
  ) {
    return buildStructuredAutoFillResult({
      objectType: 'LOCATION-POINT',
      matchedPatternId: 'structured-point-marker',
      matchedKeywords: ['point'],
    });
  }

  if (normalizedFamilyPart.includes('banger') || normalizedFamilyPart.includes('beam clamp')) {
    return buildStructuredAutoFillResult({
      objectType: 'SUPPORT',
      skey: 'HANG',
      matchedPatternId: 'structured-hanger-hardware',
      matchedKeywords: normalizedFamilyPart.includes('beam clamp') ? ['beam clamp'] : ['banger'],
    });
  }

  if (normalizedFamilyPart.includes('back to back channel')) {
    return buildStructuredAutoFillResult({
      objectType: 'SUPPORT',
      skey: 'SUPP',
      matchedPatternId: 'structured-channel-support',
      matchedKeywords: ['back to back channel'],
    });
  }

  if (normalizedFamilyPart.includes('national fig 955')) {
    return buildStructuredAutoFillResult({
      objectType: 'SUPPORT',
      skey: 'GUID',
      matchedPatternId: 'structured-national-slide-support',
      matchedKeywords: ['national fig 955'],
    });
  }

  if (
    normalizedFamilyPart.includes('mechanical equipment') ||
    normalizedFamilyPart.includes('air handling unit single end connections') ||
    (normalizedFamilyPart.startsWith('trane') && (
      normalizedFamilyPart.includes('ahu') ||
      normalizedFamilyPart.includes('idf') ||
      normalizedFamilyPart.includes('mdf')
    ))
  ) {
    return buildStructuredAutoFillResult({
      objectType: 'END-CONNECTION-EQUIPMENT',
      matchedPatternId: 'structured-equipment-family',
      matchedKeywords: ['equipment'],
    });
  }

  if (
    includesEquipmentFamilyMarker(normalizedFamilyPart, normalizedTypePart) &&
    !includesExplicitComponentMarker(normalizedFamilyPart, normalizedTypePart)
  ) {
    return buildStructuredAutoFillResult({
      objectType: 'END-CONNECTION-EQUIPMENT',
      matchedPatternId: 'structured-equipment-family-marker',
      matchedKeywords: ['equipment-family'],
      end1: 'UNDEFINED_ET',
      end2: 'UNDEFINED_ET',
    });
  }

  if (normalizedFamilyPart.includes('system breaker') || normalizedFamilyPart.includes('iso split point')) {
    return buildStructuredAutoFillResult({
      objectType: 'ISO-SPLIT-POINT',
      matchedPatternId: 'structured-iso-split-point',
      matchedKeywords: ['iso split point'],
    });
  }

  if (normalizedFamilyPart.includes('flow direction')) {
    return buildStructuredAutoFillResult({
      objectType: 'FLOW-ARROW',
      skey: 'FLOW',
      matchedPatternId: 'structured-flow-direction',
      matchedKeywords: ['flow direction'],
    });
  }

  if (normalizedFamilyPart.includes('wall penetration')) {
    return buildStructuredAutoFillResult({
      objectType: 'FLOOR-SYMBOL',
      skey: 'FLOR',
      matchedPatternId: 'structured-wall-penetration',
      matchedKeywords: ['wall penetration'],
    });
  }

  if (normalizedFamilyPart.includes('welding seam')) {
    return buildStructuredAutoFillResult({
      objectType: 'WELD',
      skey: 'BV',
      matchedPatternId: 'structured-welding-seam',
      matchedKeywords: ['welding seam'],
    });
  }

  if (normalizedFamilyPart.includes('reference dimension')) {
    return buildStructuredAutoFillResult({
      objectType: 'REFERENCE-DIMENSION',
      matchedPatternId: 'structured-reference-dimension',
      matchedKeywords: ['reference dimension'],
    });
  }

  if (normalizedFamilyPart.includes('rf pad')) {
    return buildStructuredAutoFillResult({
      objectType: 'REINFORCEMENT-PAD',
      matchedPatternId: 'structured-rf-pad',
      matchedKeywords: ['rf pad'],
    });
  }

  if (normalizedFamilyPart.includes('model text')) {
    return buildStructuredAutoFillResult({
      objectType: 'MESSAGE',
      matchedPatternId: 'structured-model-text',
      matchedKeywords: ['model text'],
    });
  }

  if (normalizedFamilyPart.includes('nozzle') || normalizedFamilyPart === 'he') {
    return buildStructuredAutoFillResult({
      objectType: 'END-CONNECTION-EQUIPMENT',
      matchedPatternId: 'structured-end-connection-equipment',
      matchedKeywords: normalizedFamilyPart === 'he' ? ['he'] : ['nozzle'],
    });
  }

  if (normalizedFamilyPart.includes('instrument')) {
    return buildStructuredAutoFillResult({
      objectType: 'INSTRUMENT',
      skey: 'ID??',
      matchedPatternId: 'structured-instrument-family',
      matchedKeywords: ['instrument'],
    });
  }

  if (normalizedFamilyPart.includes('fitting branch')) {
    return buildStructuredAutoFillResult({
      objectType: 'OLET',
      skey: 'WTBW',
      end1: 'UNDEFINED_ET',
      end2: 'UNDEFINED_ET',
      end3: 'BV',
      matchedPatternId: 'structured-fitting-branch',
      matchedKeywords: ['fitting branch'],
    });
  }

  if (normalizedTypePart.includes('iso split point')) {
    return buildStructuredAutoFillResult({
      objectType: 'ISO-SPLIT-POINT',
      matchedPatternId: 'structured-iso-split-point-type',
      matchedKeywords: ['iso split point'],
    });
  }

  return null;
}

/**
 * Main function: Auto-fill PCF attributes from a single family name
 */
export function autoFillFromFamilyName(
  familyName: string,
  options: AutoFillFromFamilyNameOptions = {}
): AutoFillResult {
  if (!familyName || typeof familyName !== 'string') {
    return { confidence: 0, matchedKeywords: [] };
  }

  const normalizedName = normalizeFamilyName(familyName);
  const tokens = normalizedName.split(/\s+/).filter(Boolean);
  const extractedAngle = extractAngle(familyName);
  const structuredResult = resolveStructuredFamilyTypeResult(familyName);

  if (structuredResult) {
    return structuredResult;
  }

  // Find best matching pattern
  let bestMatch: {
    pattern: AutoFillPattern;
    score: number;
    matchedKeywords: string[];
  } | undefined;

  for (const pattern of AUTO_FILL_PATTERNS) {
    const { score, matchedKeywords } = calculatePatternScore(
      pattern,
      normalizedName,
      familyName,
      tokens
    );

    if (score > 0 && (!bestMatch || score > bestMatch.score)) {
      bestMatch = { pattern, score, matchedKeywords };
    }
  }

  if (!bestMatch && options.includeLearned !== false) {
    return matchLearnedAutoFillPattern(
      familyName,
      useLearnedAutoFillLibraryStore.getState().entries
    ) ?? { confidence: 0, matchedKeywords: [] };
  }

  if (!bestMatch) {
    return { confidence: 0, matchedKeywords: [] };
  }

  // Calculate confidence (normalize score to 0-100 range)
  // Max theoretical score is around 150 (priority 100 * 1.5 quality * 1.x length)
  const confidence = Math.min(Math.round((bestMatch.score / 120) * 100), 100);

  // Resolve SKEY with extracted angle
  const normalizedObjectType = normalizeObjectTypeKey(bestMatch.pattern.objectType);
  const proposedSkey = resolveSkeyWithAngle(bestMatch.pattern.skey, extractedAngle);
  const skey = !extractedAngle && shouldResolveAnglePlaceholder(proposedSkey)
    ? proposedSkey
    : (resolvePreferredEtalonSkey(normalizedObjectType, proposedSkey) ?? proposedSkey);
  const etalonEndTypes = resolveEtalonEndTypes(normalizedObjectType, skey);

  // Detect if family name specifies a particular end type (e.g., "socket weld", "flanged")
  const detectedEndType = detectEndType(normalizedName, tokens);

  // Pattern may specify an end type
  const patternEndType = bestMatch.pattern.endType;

  // Build result
  const result: AutoFillResult = {
    objectType: normalizedObjectType,
    skey: skey || undefined,
    confidence,
    matchedPatternId: bestMatch.pattern.id,
    matchedKeywords: bestMatch.matchedKeywords,
    extractedAngle
  };
  const applyUniformEndType = (endType: string): void => {
    const portCount = getPortCount(normalizedObjectType);
    result.end1 = normalizeAutoFillEndType(endType, normalizedObjectType, skey);
    if (portCount >= 2) result.end2 = normalizeAutoFillEndType(endType, normalizedObjectType, skey);
    if (portCount >= 3) result.end3 = normalizeAutoFillEndType(endType, normalizedObjectType, skey);
    if (portCount >= 4) result.end4 = normalizeAutoFillEndType(endType, normalizedObjectType, skey);
  };
  const shouldPreferDetectedEndType = Boolean(
    detectedEndType &&
    !extractedAngle &&
    shouldResolveAnglePlaceholder(skey)
  );

  // Let explicit end wording win when an angle placeholder stayed unresolved.
  // Otherwise, keep the normal etalon -> pattern -> detected precedence.
  if (shouldPreferDetectedEndType && detectedEndType) {
    applyUniformEndType(detectedEndType);
  } else if (etalonEndTypes) {
    if (etalonEndTypes.end1) result.end1 = normalizeAutoFillEndType(etalonEndTypes.end1, normalizedObjectType, skey);
    if (etalonEndTypes.end2) result.end2 = normalizeAutoFillEndType(etalonEndTypes.end2, normalizedObjectType, skey);
    if (etalonEndTypes.end3) result.end3 = normalizeAutoFillEndType(etalonEndTypes.end3, normalizedObjectType, skey);
    if (etalonEndTypes.end4) result.end4 = normalizeAutoFillEndType(etalonEndTypes.end4, normalizedObjectType, skey);
  } else if (patternEndType) {
    applyUniformEndType(patternEndType);
  } else if (detectedEndType) {
    applyUniformEndType(detectedEndType);
  }

  return result;
}

/**
 * Batch auto-fill for multiple family names
 */
export function autoFillBatch(familyNames: string[]): BatchAutoFillResult {
  const results = new Map<string, AutoFillResult>();
  let matched = 0;
  let highConfidence = 0;

  for (const name of familyNames) {
    const result = autoFillFromFamilyName(name);
    results.set(name, result);

    if (result.confidence > 0) {
      matched++;
      if (result.confidence >= 70) {
        highConfidence++;
      }
    }
  }

  return {
    total: familyNames.length,
    matched,
    highConfidence,
    results
  };
}

export function resolveQuickFillAutoFillSourceName(item: Record<string, unknown> | undefined): string {
  const familyTypeKey = item?.__familyTypeKey;
  if (typeof familyTypeKey === 'string' && familyTypeKey.trim()) {
    return familyTypeKey.trim();
  }

  const familyName = item?.__familyName;
  const typeName = item?.__typeName;
  if (typeof familyName === 'string' && familyName.trim()) {
    return typeof typeName === 'string' && typeName.trim()
      ? `${familyName.trim()}:${typeName.trim()}`
      : familyName.trim();
  }

  const name = item?.Name;
  return typeof name === 'string' ? name : '';
}

function resolveQuickFillAutoFillCategoryName(item: Record<string, unknown> | undefined): string {
  const metadataCategory = item?.__category;
  if (typeof metadataCategory === 'string' && metadataCategory.trim()) {
    return metadataCategory.trim();
  }

  const category = item?.Category ?? item?.category;
  return typeof category === 'string' ? category.trim() : '';
}

function isNippleLikeAutoFillResult(result: AutoFillResult): boolean {
  return result.matchedPatternId === 'nipple' ||
    result.matchedKeywords.some((keyword) =>
      normalizeFamilyName(keyword).split(/\s+/).includes('nipple')
    );
}

function stripCategoryIncompatibleAutoFillResult(result: AutoFillResult): AutoFillResult {
  return {
    confidence: 0,
    matchedPatternId: result.matchedPatternId
      ? `${result.matchedPatternId}-category-incompatible`
      : 'category-incompatible',
    matchedKeywords: result.matchedKeywords,
    extractedAngle: result.extractedAngle,
  };
}

function constrainAutoFillResultToCategory(
  result: AutoFillResult,
  categoryName: string
): AutoFillResult {
  if (!result.objectType) {
    return result;
  }

  const compatibleObjectTypes = getCompatibleObjectTypeValuesForCategory(categoryName);
  if (!compatibleObjectTypes) {
    return result;
  }

  const objectType = normalizeObjectTypeKey(result.objectType);
  if (compatibleObjectTypes.includes(objectType)) {
    return result;
  }

  const categoryKind = resolvePcfObjectTypeCategoryKind(categoryName);
  if (categoryKind === 'pipe' || categoryKind === 'flexPipe') {
    return buildStructuredAutoFillResult({
      objectType: 'PIPE',
      matchedPatternId: `${result.matchedPatternId ?? 'auto-fill'}-category-pipe`,
      matchedKeywords: [...result.matchedKeywords, categoryName],
      confidence: result.confidence,
      end1: 'PL',
      end2: 'PL',
    });
  }

  if (categoryKind === 'mechanicalEquipment') {
    return buildStructuredAutoFillResult({
      objectType: 'END-CONNECTION-EQUIPMENT',
      matchedPatternId: `${result.matchedPatternId ?? 'auto-fill'}-category-equipment`,
      matchedKeywords: [...result.matchedKeywords, categoryName],
      confidence: result.confidence,
    });
  }

  if (categoryKind === 'pipeFitting' && objectType === 'PIPE' && isNippleLikeAutoFillResult(result)) {
    return buildStructuredAutoFillResult({
      objectType: 'COUPLING',
      skey: 'CO??',
      matchedPatternId: `${result.matchedPatternId ?? 'auto-fill'}-category-fitting-coupling`,
      matchedKeywords: [...result.matchedKeywords, categoryName],
      confidence: result.confidence,
      preserveSkey: true,
    });
  }

  return stripCategoryIncompatibleAutoFillResult(result);
}

export function autoFillFromQuickFillItem(item: Record<string, unknown> | undefined): AutoFillResult {
  return constrainAutoFillResultToCategory(
    autoFillFromFamilyName(resolveQuickFillAutoFillSourceName(item)),
    resolveQuickFillAutoFillCategoryName(item)
  );
}

export function isSkeyAutoFillColumn(label: string): boolean {
  const normalizedLabel = label.trim().toUpperCase();
  return normalizedLabel === 'SKEY' || normalizedLabel === '#PCF_SKEY' || normalizedLabel === 'PCF_SKEY';
}

/**
 * Model Text rows are classified (MESSAGE) for display, but Model Text is not
 * a loadable family and its rows carry no writable classification parameter —
 * writes for them always fail, so every save path skips them instead.
 */
export function isModelTextAutoFillResult(result: AutoFillResult): boolean {
  return result.matchedPatternId === 'structured-model-text';
}

export function isEndTypeAutoFillColumn(label: string): boolean {
  const normalizedLabel = label.trim().toUpperCase();
  return [
    'END1',
    'END-1',
    'END_1',
    'PIX_PPG_END1_TYPE',
    'END2',
    'END-2',
    'END_2',
    'PIX_PPG_END2_TYPE',
    'END3',
    'END-3',
    'END_3',
    'PIX_PPG_END3_TYPE',
    'END4',
    'END-4',
    'END_4',
    'PIX_PPG_END4_TYPE',
  ].includes(normalizedLabel);
}

function isUndefinedEndTypePlaceholder(value: unknown): boolean {
  return typeof value === 'string' && value.trim().toUpperCase() === 'UNDEFINED_ET';
}

export function canReplaceWeakAutoFillPlaceholder(
  label: string,
  currentValue: unknown,
  nextValue: unknown
): boolean {
  if (currentValue === nextValue) {
    return false;
  }

  if (
    isSkeyAutoFillColumn(label) &&
    typeof currentValue === 'string' &&
    /\?\?/.test(currentValue.trim()) &&
    typeof nextValue === 'string' &&
    !/\?\?/.test(nextValue.trim())
  ) {
    return true;
  }

  return (
    isEndTypeAutoFillColumn(label) &&
    isUndefinedEndTypePlaceholder(currentValue) &&
    !isUndefinedEndTypePlaceholder(nextValue)
  );
}

export function resolveAutoFillValueForColumn(
  result: AutoFillResult,
  label: string
): string | undefined {
  const normalizedLabel = label.trim().toUpperCase();

  switch (normalizedLabel) {
    case 'OBJECT-TYPE':
    case 'OBJECT_TYPE':
    case '#PCF_OBJECT_TYPE':
    case 'PCF_OBJECT_TYPE':
    case 'PIX_OBJECT_TYPE':
    case 'PIX_02_OBJECT_TYPE':
      return result.objectType;
    case 'SKEY':
    case '#PCF_SKEY':
    case 'PCF_SKEY':
      return result.skey;
    case 'END1':
    case 'END-1':
    case 'END_1':
    case 'PIX_PPG_END1_TYPE':
      return result.end1;
    case 'END2':
    case 'END-2':
    case 'END_2':
    case 'PIX_PPG_END2_TYPE':
      return result.end2;
    case 'END3':
    case 'END-3':
    case 'END_3':
    case 'PIX_PPG_END3_TYPE':
      return result.end3;
    case 'END4':
    case 'END-4':
    case 'END_4':
    case 'PIX_PPG_END4_TYPE':
      return result.end4;
    default:
      return undefined;
  }
}

/**
 * Get a preview summary of auto-fill results
 */
export function getAutoFillPreview(
  familyNames: string[]
): { matches: number; total: number; preview: string } {
  const batchResult = autoFillBatch(familyNames);

  const preview = batchResult.matched > 0
    ? `Found matches for ${batchResult.matched} of ${batchResult.total} families ` +
      `(${batchResult.highConfidence} high confidence)`
    : `No matches found for ${batchResult.total} families`;

  return {
    matches: batchResult.matched,
    total: batchResult.total,
    preview
  };
}

/**
 * Apply auto-fill results to QuickFill data format
 * Maps AutoFillResult properties to QuickFillItem properties
 * Only fills empty cells - does not overwrite existing values
 */
export function applyAutoFillToQuickFillItem<T extends Record<string, unknown>>(
  item: T,
  result: AutoFillResult,
  propertyLabels: string[],
  mode: QuickFillAutoFillMode = 'fillEmpty'
): T {
  const updatedItem = { ...item };
  let changed = false;

  // Helper to check if a cell is empty
  const isEmpty = (value: unknown): boolean => {
    return value === undefined || value === null || value === '';
  };

  const shouldApplyValue = (
    label: string,
    currentValue: unknown,
    nextValue: unknown
  ): boolean => {
    if (nextValue === undefined || nextValue === null || nextValue === '') {
      return isIgnoreEmptySkeyAutoFillValue(result, label, nextValue, mode);
    }

    if (mode === 'overwrite') {
      return currentValue !== nextValue;
    }

    return (
      isEmpty(currentValue) ||
      canReplaceWeakAutoFillPlaceholder(label, currentValue, nextValue)
    );
  };

  for (const label of propertyLabels) {
    const nextValue = resolveAutoFillValueForColumn(result, label);
    if (shouldApplyValue(label, item[label as keyof T], nextValue)) {
      updatedItem[label as keyof T] = nextValue as T[keyof T];
      changed = true;
    }
  }

  return changed ? updatedItem : item;
}

/**
 * Extracts the family name part from a "FamilyName:TypeName" string
 */
export function extractFamilyNameFromFullName(fullName: string): string {
  if (!fullName) return '';

  // Split by colon to separate family name from type name
  const colonIndex = fullName.indexOf(':');
  if (colonIndex !== -1) {
    return fullName.substring(0, colonIndex);
  }

  return fullName;
}

/**
 * Debug utility: Returns detailed matching info for a family name
 */
export function debugAutoFill(familyName: string): {
  normalized: string;
  extractedAngle?: number;
  allMatches: Array<{
    patternId: string;
    objectType: string;
    score: number;
    keywords: string[];
  }>;
  detectedEndType?: string;
  finalResult: AutoFillResult;
} {
  const normalizedName = normalizeFamilyName(familyName);
  const tokens = normalizedName.split(/\s+/).filter(Boolean);
  const extractedAngle = extractAngle(familyName);
  const detectedEndType = detectEndType(normalizedName, tokens);

  const allMatches: Array<{
    patternId: string;
    objectType: string;
    score: number;
    keywords: string[];
  }> = [];

  for (const pattern of AUTO_FILL_PATTERNS) {
    const { score, matchedKeywords } = calculatePatternScore(
      pattern,
      normalizedName,
      familyName,
      tokens
    );

    if (score > 0) {
      allMatches.push({
        patternId: pattern.id,
        objectType: pattern.objectType,
        score,
        keywords: matchedKeywords
      });
    }
  }

  // Sort by score descending
  allMatches.sort((a, b) => b.score - a.score);

  return {
    normalized: normalizedName,
    extractedAngle,
    allMatches,
    detectedEndType,
    finalResult: autoFillFromFamilyName(familyName)
  };
}

